import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

/**
 * Marks a scroll container with `scroll-fade-top` / `scroll-fade-bottom` while there is content
 * hidden above / below, so the styles can fade that edge (content appears to slide under it).
 * Follows scrolling and size changes of the container and its content (e.g. sections expanding).
 */
@Directive({
  selector: '[appScrollFade]',
  standalone: true,
})
export class ScrollFadeDirective implements AfterViewInit, OnDestroy {
  private resizeObserver?: ResizeObserver;
  private readonly onScroll = () => this.update();

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone) { }

  ngAfterViewInit(): void {
    const element = this.host.nativeElement;
    // Purely visual: no change detection needed.
    this.zone.runOutsideAngular(() => {
      element.addEventListener('scroll', this.onScroll, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(element);
        Array.from(element.children).forEach((child) => this.resizeObserver!.observe(child));
      }
    });
    this.update();
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  update(): void {
    const element = this.host.nativeElement;
    const hiddenAbove = element.scrollTop > 1;
    const hiddenBelow = element.scrollTop + element.clientHeight < element.scrollHeight - 1;
    element.classList.toggle('scroll-fade-top', hiddenAbove);
    element.classList.toggle('scroll-fade-bottom', hiddenBelow);
  }
}
